import React from 'react';
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Box,
} from '@mui/material';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

interface CustomTableProps {
  columns: any[];
  data: any[];
  renderSubRowComponent?: (row: any, columnId: string) => React.ReactNode;
  expandedState?: { rowIndex: number | null; columnIds: Set<string> };
  onCellClick?: (rowIndex: number, columnId: string, row: any) => void;
  onRowClick?: (row: any) => void; // New prop for row click
  expandableColumns?: string[];
  borderRadiusIsPresent?: boolean;
}

function CustomTable({
  columns,
  data,
  renderSubRowComponent,
  expandedState,
  onCellClick,
  onRowClick,
  expandableColumns = [],
  borderRadiusIsPresent = true,
}: CustomTableProps) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleRowClick = (row: any, event: React.MouseEvent) => {
    // Only trigger row click if it's not from a cell click in expandable columns
    const clickedCell = (event.target as HTMLElement).closest('td');
    const cellIndex = clickedCell?.cellIndex;
    const isExpandableCell =
      cellIndex !== undefined &&
      expandableColumns.includes(row.getVisibleCells()[cellIndex].column.id);

    if (!isExpandableCell && onRowClick) {
      onRowClick(row.original);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        overflowX: { xs: 'auto', md: 'unset' }, // Enable horizontal scroll only on small screens
      }}
    >
      <TableContainer
        component={Paper}
        sx={{
          border: '1px solid #ddd',
          borderRadius: borderRadiusIsPresent ? 2 : null,
          overflow: 'hidden',
          width: { xs: 'fit-content', md: '100%' }, // Scrollable width on mobile, full width on desktop
        }}
      >
        <Table sx={{ minWidth: { xs: '800px', md: '100%' } }}>
          {' '}
          {/* Prevent shrink on mobile */}
          {/* Table Head */}
          <TableHead sx={{ backgroundColor: '#484848' }}>
            {table?.getHeaderGroups()?.map((headerGroup) => (
              <TableRow key={headerGroup?.id}>
                {headerGroup?.headers?.map((header) => (
                  <TableCell
                    key={header?.id}
                    sx={{
                      fontWeight: 'bold',
                      fontSize: '14px',
                      color: 'white',
                      padding: '8px',
                      whiteSpace: 'nowrap', // Prevents wrapping
                    }}
                  >
                    {header?.isPlaceholder
                      ? null
                      : flexRender(
                          header?.column?.columnDef?.header,
                          header?.getContext()
                        )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableHead>
          {/* Table Body */}
          <TableBody>
            {table?.getRowModel()?.rows?.map((row) => (
              <React.Fragment key={row?.id}>
                {/* Data Rows */}
                <TableRow
                  hover
                  onClick={(e) => handleRowClick(row, e)}
                  sx={{ cursor: onRowClick ? 'pointer' : 'default' }}
                >
                  {row?.getVisibleCells()?.map((cell) => (
                    <TableCell
                      key={cell?.id}
                      sx={{
                        fontSize: '13px',
                        paddingY: '20px',
                        paddingX: '10px',
                        whiteSpace: 'nowrap', // Prevents text wrapping
                      }}
                      onClick={(e) => {
                        if (
                          expandableColumns?.includes(cell?.column?.id) &&
                          onCellClick
                        ) {
                          e.stopPropagation(); // Prevent row click when cell is clicked
                          onCellClick(
                            row?.index,
                            cell?.column?.id,
                            row?.original
                          );
                        }
                      }}
                    >
                      {flexRender(
                        cell?.column?.columnDef?.cell,
                        cell?.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                {/* Sub-rows */}
                {expandedState &&
                  expandedState?.rowIndex === row?.index &&
                  Array.from(expandedState?.columnIds)?.map((columnId) => (
                    <TableRow key={`subrow-${columnId}`}>
                      <TableCell colSpan={row?.getVisibleCells()?.length}>
                        {renderSubRowComponent &&
                          renderSubRowComponent(row?.original, columnId)}
                      </TableCell>
                    </TableRow>
                  ))}
              </React.Fragment>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default CustomTable;
